<script lang="ts">
  import { onDestroy, onMount, tick } from "svelte"
  import CalendarDaySummary from "../../_shared/_modules/calendar-day-summary"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import { escapeTooltipHtml } from "../../../../../../../_shared/_logic/rich-tooltips"
  import type {
    BoardActions,
    BoardColumn,
    BoardEvent,
    BoardInteractionConfig,
  } from "../../../../_logic/calendar-types"
  import { buildOverviewWeeks, isOverviewToday } from "./_logic/overviewProjection"
  import { buildOverviewMove, formatZonedTime, zonedDayKey } from "../../../../_logic/overview-move"

  let {
    columns,
    visibleDays,
    timezone,
    now,
    actions,
    interaction,
    readOnly = false,
    isLoading = false,
    editableEmployeeIds = null,
    showUpdateSuccessNotification = true,
  }: {
    columns: BoardColumn[]
    visibleDays: Date[]
    timezone: string
    now: Date
    actions: BoardActions
    interaction: BoardInteractionConfig
    readOnly?: boolean
    isLoading?: boolean
    editableEmployeeIds?: string[] | null
    showUpdateSuccessNotification?: boolean
  } = $props()
  const weeks = $derived(buildOverviewWeeks(columns, visibleDays))
  const days = $derived(weeks.flat().filter((day) => day.included))
  const employees = $derived(weeks[0]?.[0]?.employees ?? [])
  let root: HTMLDivElement
  let moving = $state<{ item: BoardEvent; target: string | null; keyboard: boolean } | null>(null)
  let pending: { item: BoardEvent; pointerId: number; x: number; y: number } | null = null
  let suppressClick = false
  let pendingIds = $state<string[]>([])
  let message = $state("")
  let scrollFrame: number | null = null
  let pointerPosition: { x: number; y: number } | null = null
  const dayLabel = (date: Date) =>
    date.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })
  const canMove = (item: BoardEvent) =>
    !readOnly &&
    !isLoading &&
    !interaction.readOnly &&
    interaction.canDirectlyEditEvents &&
    item.event.event_type === "APPOINTMENT" &&
    !pendingIds.includes(item.event.id) &&
    (editableEmployeeIds == null || editableEmployeeIds.includes(item.event.employee_id ?? "")) &&
    (interaction.canMoveEvent?.(item) ?? true) &&
    (interaction.canMoveEventToEmployee?.(item, item.event.employee_id ?? null) ?? true)
  const timeLabel = (item: BoardEvent) =>
    item.event.flex_type === "SOFT"
      ? `${formatZonedTime(item.start, timezone)}–${formatZonedTime(item.end, timezone)}`
      : formatZonedTime(new Date(item.event.start_at), timezone)
  const moveLabel = $derived.by(() => {
    if (!moving?.target) return "Zum Verschieben einen anderen Tag auswählen."

    try {
      const payload = buildOverviewMove(moving.item, moving.target, timezone)

      if (!payload) return "Gleicher Tag – der Termin bleibt unverändert."

      const day = days.find((day) => day.key === moving?.target)

      return `${day ? dayLabel(day.date) : moving.target}, ${formatZonedTime(payload.start, timezone)}–${formatZonedTime(payload.end, timezone)}${moving.item.event.flex_type === "SOFT" ? " · wird fester Termin" : ""}`
    } catch (error) {
      return error instanceof Error ? error.message : "Dieser Tag ist nicht verfügbar."
    }
  })

  function removePointerListeners() {
    window.removeEventListener("pointermove", pointerMove)
    window.removeEventListener("pointerup", pointerUp)
    window.removeEventListener("pointercancel", cancel)
    pending = null
    pointerPosition = null

    if (scrollFrame !== null) cancelAnimationFrame(scrollFrame)

    scrollFrame = null
  }
  function cancel() {
    removePointerListeners()
    moving = null
    message = ""
  }
  function pointerDown(item: BoardEvent, event: PointerEvent) {
    suppressClick = false

    if (!canMove(item) || event.button !== 0) return

    pending = { item, pointerId: event.pointerId, x: event.clientX, y: event.clientY }
    window.addEventListener("pointermove", pointerMove)
    window.addEventListener("pointerup", pointerUp)
    window.addEventListener("pointercancel", cancel)
  }
  function pointerMove(event: PointerEvent) {
    if (!pending || pending.pointerId !== event.pointerId) return

    if (!moving && Math.hypot(event.clientX - pending.x, event.clientY - pending.y) < 6) return

    suppressClick = true
    pointerPosition = { x: event.clientX, y: event.clientY }
    updatePointerTarget()

    if (scrollFrame === null) scrollFrame = requestAnimationFrame(scrollWhileMoving)

    event.preventDefault()
  }
  function updatePointerTarget() {
    if (!pointerPosition || !pending) return

    const element = document
      .elementFromPoint(pointerPosition.x, pointerPosition.y)
      ?.closest<HTMLElement>("[data-overview-day]")
    const target = element && root.contains(element) ? (element.dataset.overviewDay ?? null) : null
    moving = { item: pending.item, target, keyboard: false }
  }
  function scrollWhileMoving() {
    scrollFrame = null

    if (!moving || !pointerPosition) return

    let parent = root.parentElement
    while (
      parent &&
      (!/(auto|scroll)/.test(getComputedStyle(parent).overflowY) ||
        parent.scrollHeight <= parent.clientHeight)
    )
      parent = parent.parentElement
    const bounds = parent?.getBoundingClientRect()
    const top = Math.max(0, bounds?.top ?? 0),
      bottom = Math.min(window.innerHeight, bounds?.bottom ?? window.innerHeight)
    const delta = pointerPosition.y < top + 48 ? -10 : pointerPosition.y > bottom - 48 ? 10 : 0

    if (delta) {
      ;(parent ?? document.scrollingElement)?.scrollBy(0, delta)
      updatePointerTarget()
    }

    scrollFrame = requestAnimationFrame(scrollWhileMoving)
  }
  function pointerUp(event: PointerEvent) {
    if (!pending || pending.pointerId !== event.pointerId) return

    removePointerListeners()

    if (moving) void commitMove()
  }
  async function commitMove() {
    const move = moving
    moving = null

    if (!move?.target || !canMove(move.item)) return

    try {
      const payload = buildOverviewMove(move.item, move.target, timezone)

      if (!payload) return

      pendingIds = [...pendingIds, move.item.event.id]
      const accepted = await actions.updateEvent(payload)

      if (accepted === false) return

      if (showUpdateSuccessNotification)
        newNotification({ class: "success", html: "Termin verschoben." })

      message = "Termin verschoben."
    } catch (error) {
      message =
        error instanceof Error ? error.message : "Der Termin konnte nicht verschoben werden."
      newNotification({ class: "error", html: escapeTooltipHtml(message) })
    } finally {
      pendingIds = pendingIds.filter((id) => id !== move.item.event.id)

      if (move.keyboard) {
        await tick()
        const buttons = root?.querySelectorAll<HTMLButtonElement>("[data-overview-event]") ?? []
        Array.from(buttons)
          .find((button) => button.dataset.overviewEvent === move.item.event.id)
          ?.focus()
      }
    }
  }
  function startKeyboardMove(item: BoardEvent) {
    if (!canMove(item)) return

    moving = { item, target: zonedDayKey(new Date(item.event.start_at), timezone), keyboard: true }
  }
  function keyDown(event: KeyboardEvent) {
    if (!moving) return

    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      cancel()

      return
    }

    if (!moving.keyboard) return

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      void commitMove()

      return
    }

    const delta: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    }

    if (event.key in delta) {
      event.preventDefault()
      const index = days.findIndex((day) => day.key === moving?.target)
      const next = days[Math.max(0, Math.min(days.length - 1, index + delta[event.key]))]

      if (next) moving = { ...moving, target: next.key }
    }
  }
  onMount(() => {
    window.addEventListener("keydown", keyDown, true)

    return () => window.removeEventListener("keydown", keyDown, true)
  })
  onDestroy(removePointerListeners)
</script>

<div class="overview" bind:this={root} aria-label="Kalenderübersicht" aria-busy={isLoading}>
  {#if employees.length === 1}<div class="single-employee">{employees[0].employee.label}</div>{/if}
  <div class="weekday-head" aria-hidden="true">
    {#each ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"] as day}<span
        >{day}</span
      >{/each}
  </div>
  {#if moving}<div class="move-hint" role="status">{moveLabel}</div>{/if}
  <span class="sr-only" aria-live="polite">{message}</span>
  {#each weeks as week (week[0].key)}
    <div class="week">
      {#each week as day (day.key)}
        <section
          class="day"
          class:outside={!day.included}
          class:weekend={[0, 6].includes(day.date.getDay())}
          class:target={moving?.target === day.key}
          data-overview-day={day.included ? day.key : undefined}
          aria-label={dayLabel(day.date)}
        >
          <h3 class:today={isOverviewToday(day.key, now, timezone)}>
            <span class="short-date"
              >{day.date.toLocaleDateString("de-DE", { day: "numeric", month: "short" })}</span
            >
            <span class="full-date">{dayLabel(day.date)}</span>
          </h3>
          {#if day.included}
            {#each day.employees as group (group.employee.id)}
              <div
                class="employee"
                class:day-off={group.dayOff}
                class:employee-target={moving?.target === day.key &&
                  moving.item.event.employee_id === group.employee.id}
              >
                {#if employees.length > 1}<h4>{group.employee.label}</h4>{/if}
                <CalendarDaySummary summary={group.daySummary} />
                {#each group.events as item (item.event.id)}
                  <div
                    class="event-row"
                    class:flexible={item.event.flex_type === "SOFT"}
                    class:dimmed={item.dimmed}
                    class:moving={moving?.item.event.id === item.event.id}
                    class:pending={pendingIds.includes(item.event.id)}
                  >
                    <button
                      type="button"
                      class="event"
                      data-overview-event={item.event.id}
                      aria-label={`${timeLabel(item)} ${item.addressLabel || item.title}`}
                      use:tooltip={{
                        content: escapeTooltipHtml(
                          `${timeLabel(item)} ${item.addressLabel || item.title}`,
                        ),
                      }}
                      onpointerdown={(event) => pointerDown(item, event)}
                      onclick={() => {
                        if (suppressClick) {
                          suppressClick = false

                          return
                        }

                        actions.openEvent(item.event)
                      }}
                      onkeydown={(event) => {
                        if (event.key === " " && !moving) {
                          event.preventDefault()
                          event.stopPropagation()
                          startKeyboardMove(item)
                        }
                      }}
                    >
                      <time>{timeLabel(item)}</time><span>{item.addressLabel || item.title}</span>
                    </button>
                    {#if canMove(item)}
                      <button
                        type="button"
                        class="move-button"
                        aria-label={`Termin verschieben: ${item.addressLabel || item.title}`}
                        onclick={() => {
                          if (suppressClick) {
                            suppressClick = false

                            return
                          }

                          startKeyboardMove(item)
                        }}
                        onpointerdown={(event) => pointerDown(item, event)}>⠿</button
                      >
                    {/if}
                  </div>
                {/each}
                {#each group.busy as busy (`${busy.calendar_id}:${busy.calendar_entry_id}`)}
                  <div class="busy">
                    {busy.all_day
                      ? "Ganztägig"
                      : `${formatZonedTime(busy.start, timezone)}–${formatZonedTime(busy.end, timezone)}`}
                    Belegt
                  </div>
                {/each}
                {#if !group.events.length && !group.busy.length}<span class="empty"
                    >{group.dayOff ? "Arbeitsfrei" : "Keine Termine"}</span
                  >{/if}
              </div>
            {/each}
          {/if}
        </section>
      {/each}
    </div>
  {/each}
  {#if !weeks.length}<p>Für diesen Zeitraum sind keine Tage ausgewählt.</p>{/if}
</div>

<style>
  .overview {
    container-type: inline-size;
    background: var(--cal-bg, #fff);
    color: var(--cal-text, #2f4858);
    border: 1px solid var(--cal-border-strong, #ccd4d8);
    border-radius: 8px;
  }
  .single-employee {
    padding: 10px 12px;
    font-weight: 600;
    font-size: 14px;
    color: var(--cal-text, #2f4858);
  }
  .weekday-head,
  .week {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
  .weekday-head {
    background: var(--cal-surface, #eee);
    font-size: 12px;
    font-weight: 600;
  }
  .weekday-head span {
    padding: 10px;
    color: var(--cal-text-muted, #617481);
    line-height: 18px;
  }
  .day {
    min-width: 0;
    min-height: 110px;
    border-top: 1px solid var(--cal-border-strong, #ccd4d8);
    border-right: 1px solid var(--cal-border-strong, #ccd4d8);
    padding: 8px 5px 10px;
  }
  .day:last-child {
    border-right: 0;
  }
  .weekend {
    background: color-mix(in srgb, var(--cal-text, #2f4858) 3%, transparent);
  }
  .outside {
    background: var(--cal-surface, #f3f5f6);
  }
  /* Keep the light calendar surface independent of the surrounding dark overlay headings. */
  .overview .day h3 {
    color: var(--text-invers-100, #2f4858);
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
    margin: 0 3px 8px;
    padding: 3px 6px;
    width: fit-content;
    border-radius: 12px;
  }
  h3 span {
    color: inherit;
    line-height: inherit;
  }
  .overview .outside h3 {
    color: var(--cal-text-muted, #617481);
  }
  .overview .day h3.today {
    background: var(--cal-text, #2f4858);
    color: var(--cal-bg, #fff);
  }
  h4 {
    color: var(--cal-text, #2f4858);
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    margin: 4px 4px 6px;
    overflow-wrap: anywhere;
  }
  .employee {
    padding: 2px 0 6px;
  }
  .employee + .employee {
    border-top: 1px dashed var(--cal-border-strong, #ccd4d8);
    margin-top: 6px;
    padding-top: 8px;
  }
  .target {
    background: color-mix(in srgb, var(--primary-100, #00ce60) 8%, var(--cal-bg, #fff));
  }
  .employee-target {
    outline: 2px solid var(--primary-100, #00ce60);
    outline-offset: -2px;
    border-radius: 3px;
  }
  .event-row {
    position: relative;
    min-width: 0;
    margin: 2px 0;
    border-left: 2px solid var(--cal-accent, #2f4858);
    border-radius: 5px;
    background: color-mix(in srgb, var(--cal-text, #2f4858) 5%, var(--cal-bg, #fff));
    color: var(--cal-text, #2f4858);
    transition: background-color 120ms ease;
  }
  .event-row:hover,
  .event-row:focus-within {
    background: color-mix(in srgb, var(--cal-text, #2f4858) 10%, var(--cal-bg, #fff));
  }
  .flexible {
    border-left-style: dashed;
  }
  .event {
    display: flex;
    gap: 5px;
    width: 100%;
    min-width: 0;
    align-items: center;
    height: 24px;
    padding: 3px 16px 3px 4px;
    border: 0;
    background: transparent;
    color: var(--cal-text, #2f4858);
    font-size: 12px;
    line-height: 18px;
    text-align: left;
    cursor: pointer;
    touch-action: pan-y;
  }
  time {
    color: var(--cal-text, #2f4858);
    line-height: inherit;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }
  .event span {
    color: var(--cal-text, #2f4858);
    line-height: inherit;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .move-button {
    position: absolute;
    right: 0;
    top: 0;
    height: 24px;
    width: 16px;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--cal-text, #2f4858);
    opacity: 0.1;
    cursor: grab;
  }
  .event-row:hover .move-button,
  .event-row:focus-within .move-button {
    opacity: 1;
  }
  .event:focus-visible,
  .move-button:focus-visible {
    outline: 2px solid var(--primary-100, #00ce60);
    outline-offset: -2px;
  }
  .dimmed,
  .moving,
  .pending {
    opacity: 0.55;
  }
  .busy,
  .empty {
    display: block;
    padding: 3px 4px;
    font-size: 12px;
    line-height: 18px;
    overflow-wrap: anywhere;
  }
  .busy {
    color: var(--cal-text, #2f4858);
    background: repeating-linear-gradient(135deg, transparent 0 5px, #8899aa22 5px 10px);
  }
  .empty {
    color: var(--cal-text-muted, #587089);
  }
  .move-hint {
    position: sticky;
    top: 0;
    z-index: 10;
    padding: 10px;
    background: var(--cal-bg, #fff);
    border-bottom: 2px solid var(--primary-100, #00ce60);
    font-size: 13px;
  }
  .full-date {
    display: none;
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
  }
  @container (max-width:839px) {
    .weekday-head,
    .outside,
    .short-date {
      display: none;
    }
    .full-date {
      display: inline;
    }
    .week {
      display: block;
    }
    .day {
      border-right: 0;
      min-height: 0;
      padding: 10px;
    }
  }
  @media (pointer: coarse) {
    .event,
    .move-button {
      min-height: 44px;
    }
    .event {
      align-items: center;
    }
  }
</style>
